import type * as Sentry from "@sentry/core";
import type { App } from "vue";

import { mergeDeepRight } from "ramda";

import { getEnv } from "./solvariEnv";

type AddDataToProject = () => Pick<Sentry.Event, "contexts" | "tags">;

type CaptureArgs = Parameters<typeof Sentry.captureException>;
type CaptureException = (...args: CaptureArgs) => void;

type GlobalSentry = Partial<{
  captureException: CaptureException;
  captureLog: CaptureException;
  trackVueErrors: (app: App) => void;
}>;
type SentryInstance = Required<GlobalSentry>;

declare global {
  var sentry: GlobalSentry | undefined;

  var sentryProjects: Record<string, AddDataToProject> | undefined;
}

/**
 * Retrieves the global Sentry instance to avoid importing Sentry in every micro frontend.
 * Merges optional context with capture calls. Logs to console in non-production environments.
 *
 * @param instanceContext - Optional default context merged with all capture calls
 * @returns Sentry instance with captureException, captureLog, and trackVueErrors methods
 * @example const sentry = getSentry({ tags: { project: 'my-app' } }); sentry.captureException(error);
 */
function getSentry(instanceContext?: CaptureArgs[1]): SentryInstance {
  type Context = Exclude<CaptureArgs[1], undefined>;
  function mergeContext(captureContext?: Context): Context | undefined {
    if (!instanceContext || !captureContext) {
      return instanceContext ?? captureContext;
    }
    return mergeDeepRight(
      instanceContext as Record<string, unknown>,
      captureContext as Record<string, unknown>,
    );
  }

  return {
    captureException: (error: CaptureArgs[0], hint?: CaptureArgs[1]) => {
      const context = mergeContext(hint);
      globalThis.sentry?.captureException?.(error, context);
      if (!globalThis.sentry || getEnv().config.envType !== "production") {
        console.error(error, context);
      }
    },
    captureLog: (error: CaptureArgs[0], hint?: CaptureArgs[1]) => {
      const context = mergeContext(hint);
      globalThis.sentry?.captureLog?.(error, context);
      if (!globalThis.sentry || getEnv().config.envType !== "production") {
        console.warn(error, context);
      }
    },
    trackVueErrors: globalThis.sentry?.trackVueErrors ?? trackVueErrorsFallback,
  };
}

/**
 * Registers project-specific context/tag data provider for Sentry events.
 * Function is called when Sentry captures events to add project-specific metadata.
 *
 * @param project - Project identifier (argus-fe, company-registration-form, contact-form, lead-forms, mkt-ab-testing)
 * @param addData - Function returning contexts and tags to attach to Sentry events
 * @example addDataToSentryProject('my-app', () => ({ contexts: { user: userData }, tags: { version: '1.0' } }));
 */
function addDataToSentryProject(
  project:
    | "argus-fe"
    | "company-registration-form"
    | "contact-form"
    | "lead-forms"
    | "mkt-ab-testing",
  addData: AddDataToProject,
) {
  globalThis.sentryProjects = globalThis.sentryProjects ?? {};
  globalThis.sentryProjects[project] = addData;
}

function trackVueErrorsFallback(vueApp: App) {
  vueApp.config.errorHandler = (error) => getSentry().captureException(error);
}

export { addDataToSentryProject, getSentry };
export type { AddDataToProject, CaptureException, SentryInstance };
